import { useCallback, useState } from 'react';
import type { Project } from '../../domain/schemas';
import { useProject } from '../../state/projectContext';

/**
 * Applies a project operation and turns OpError (limits, missing items) into a visible,
 * per-panel error message instead of an exception.
 */
export function useOpRunner(): { apply: (op: (p: Project) => Project) => boolean; error: string | null; clearError: () => void } {
  const { update, session } = useProject();
  const [error, setError] = useState<string | null>(null);
  const apply = useCallback(
    (op: (p: Project) => Project) => {
      const current = session.snapshot.project;
      if (!current) return false;
      try {
        const next = op(current);
        setError(null);
        update(() => next);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That change could not be applied.');
        return false;
      }
    },
    [session, update],
  );
  return { apply, error, clearError: useCallback(() => setError(null), []) };
}
